import styled, { css } from "styled-components";

import { IGroupBy, IGroupByListColumn } from "models";
import { EntityKind } from "models/entityKind";

import FieldList from "./FieldList";
import { useGroupByStore } from "./GroupByStoreProvider";
import MetadataFilterToolbar from "./MetadataFilterToolbar";
import OrgGeoCategory from "./OrgGeoCategory";
import OrgGeoSimsCategory from "./OrgGeoSimsCategory";
import PadMetricsToolbar from "./PadMetricsToolbar";
import ProductToolbar from "./ProductToolbar";
import ProjectFieldToolbar from "./ProjectFieldToolbar";
import ShapefileCategory from "./ShapefileCategory";
import UserFieldToolbar from "./UserFieldToolbar";
import {
  selectEntityKind,
  selectIsFiltering,
  selectLiveSelectedCategory
} from "./groupBySelectors";
import { useSelectedCategory } from "./hooks";
import { ProjectPermissionsProvider } from "./hooks/useProjectPermissionsContext";

export const IGNORE_METADATA_FILTERS_FOR_GROUPS = ["Performance", "Forecast"];
export const MAX_SEARCH_RESULTS = 50;

export interface FieldSelectionData {
  field: IGroupByListColumn;
  onChange?: (
    value: IGroupBy | IGroupByListColumn | Record<string, unknown>,
    isForecastToggleOn?: boolean,
    selectedForecastFolder?: string,
    selectedForecastFolderName?: string,
    selectedPdenSource?: number
  ) => void;
  isForecastToggleOn?: boolean;
  selectedForecastFolder?: string;
  selectedForecastFolderName?: string;
  selectedPdenSource?: number;
}
/* eslint-disable @typescript-eslint/no-explicit-any */
interface FilterableFieldListProps {
  active?: boolean;
  focusedIndex?: Maybe<number>;
  toolbarActive?: boolean;
  toolbarFocusedIndex?: Maybe<number>;
  tabsActive?: boolean;
  tabsFocusedIndex?: Maybe<number>;
  isHidingManagement?: boolean;
  isDisablingManagement?: boolean;
  onFieldItemRef?: (index: number, el: Maybe<HTMLElement>) => void;
  onToolbarSelectRef?: (index: number, selectEl: Maybe<any>) => void;
  onTabItemRef?: (index: number, el: Maybe<HTMLElement>) => void;
  onOrgGeoFieldsAvailable?: (fieldsCount: number) => void;
  onFieldClick?: (fieldData: FieldSelectionData) => void;
  hideDateRange?: boolean;
  hideSourceControls?: boolean;
  hideForecastTrumping?: boolean;
  onFieldDoubleClick?: (fieldData: FieldSelectionData) => void;
}

function FilterableFieldList({
  active = false,
  focusedIndex = null,
  toolbarActive = false,
  toolbarFocusedIndex = null,
  tabsActive = false,
  tabsFocusedIndex = null,
  isHidingManagement = false,
  isDisablingManagement = false,
  onFieldItemRef,
  onToolbarSelectRef,
  onTabItemRef,
  onOrgGeoFieldsAvailable,
  onFieldClick,
  hideDateRange = false,
  hideSourceControls = false,
  hideForecastTrumping = false,
  onFieldDoubleClick
}: FilterableFieldListProps): JSX.Element {
  const isFiltering = useGroupByStore(selectIsFiltering);
  const selectedCategory = useGroupByStore(selectLiveSelectedCategory);
  const entityKind = useGroupByStore(selectEntityKind);
  const {
    isProjectDefinedColumn,
    isOrgGeoDefinedColumn,
    isOrgGeoSimsDefinedColumn,
    isShapefileCategory,
    isPadMetricsCategory
  } = useSelectedCategory();

  // Determine which toolbar components should render
  const shouldIgnoreMetadataFilters =
    isPadMetricsCategory ||
    selectedCategory?.metadataFilters?.length === 0 ||
    IGNORE_METADATA_FILTERS_FOR_GROUPS.includes(selectedCategory?.name);

  // These indices inform GroupByWrapper's focusedIndex how many element of the toolbar there are
  const getToolbarIndices = () => {
    let index = 0;
    const indices: Record<string, number> = {};

    if (!shouldIgnoreMetadataFilters) {
      indices.metadataFilter = index;
      index += selectedCategory?.metadataFilters?.length || 0;
    }

    if (isPadMetricsCategory) {
      indices.padMetrics = index;
      index +=
        (selectedCategory?.metadataFilters?.length || 0) +
        (hideSourceControls
          ? 0
          : 2 + // forecast select + rescat select
            (entityKind === EntityKind.Well ? 1 : 0)); // pden select (Well only)
    }

    indices.product = index;

    if (isProjectDefinedColumn) {
      indices.project = index;
      index += 1;
    }

    indices.user = index;
    index += 3; // UserField has 3 buttons: delete, edit, add

    if (isOrgGeoDefinedColumn) {
      indices.orgGeo = 0;
    } else if (isOrgGeoSimsDefinedColumn) {
      indices.orgSims = 0;
    } else if (isShapefileCategory) {
      indices.shapefile = 0;
    }

    return indices;
  };

  const toolbarIndices = getToolbarIndices();

  return (
    <ProjectPermissionsProvider>
      <Wrapper>
        <ToolbarContainer isVisible={!isFiltering}>
          {!shouldIgnoreMetadataFilters && (
            <MetadataFilterToolbar
              toolbarActive={toolbarActive}
              toolbarFocusedIndex={
                toolbarFocusedIndex !== null &&
                toolbarIndices.metadataFilter !== undefined
                  ? toolbarFocusedIndex - toolbarIndices.metadataFilter
                  : null
              }
              onSelectRef={(index: number, selectEl: Maybe<HTMLElement>) => {
                if (onToolbarSelectRef && toolbarIndices.metadataFilter !== undefined) {
                  onToolbarSelectRef(toolbarIndices.metadataFilter + index, selectEl);
                }
              }}
            />
          )}
          {isPadMetricsCategory && (
            <PadMetricsToolbar
              toolbarActive={toolbarActive}
              toolbarFocusedIndex={
                toolbarFocusedIndex !== null && toolbarIndices.padMetrics !== undefined
                  ? toolbarFocusedIndex - toolbarIndices.padMetrics
                  : null
              }
              onSelectRef={(index: number, selectEl: Maybe<HTMLElement>) => {
                if (onToolbarSelectRef && toolbarIndices.padMetrics !== undefined) {
                  onToolbarSelectRef(toolbarIndices.padMetrics + index, selectEl);
                }
              }}
              hideForecastTrumping={hideForecastTrumping}
              hideSourceControls={hideSourceControls}
            />
          )}
          <ProductToolbar
            toolbarActive={toolbarActive}
            toolbarFocusedIndex={
              toolbarFocusedIndex !== null
                ? toolbarFocusedIndex - toolbarIndices.product
                : null
            }
            onSelectRef={(index: number, selectEl: Maybe<HTMLElement>) => {
              if (onToolbarSelectRef) {
                onToolbarSelectRef(toolbarIndices.product + index, selectEl);
              }
            }}
            hideDateRange={hideDateRange}
            hideSourceControls={hideSourceControls}
            hideForecastTrumping={hideForecastTrumping}
          />
          {isProjectDefinedColumn && !isHidingManagement && (
            <ProjectFieldToolbar
              toolbarActive={toolbarActive}
              toolbarFocusedIndex={
                toolbarFocusedIndex !== null && toolbarIndices.project !== undefined
                  ? toolbarFocusedIndex - toolbarIndices.project
                  : null
              }
              onSelectRef={(index: number, selectEl: Maybe<any>) => {
                if (onToolbarSelectRef && toolbarIndices.project !== undefined) {
                  onToolbarSelectRef(toolbarIndices.project + index, selectEl);
                }
              }}
            />
          )}
          {!isHidingManagement && (
            <UserFieldToolbar
              toolbarActive={toolbarActive}
              toolbarFocusedIndex={
                toolbarFocusedIndex !== null
                  ? toolbarFocusedIndex - toolbarIndices.user
                  : null
              }
              onSelectRef={(index: number, selectEl: Maybe<any>) => {
                if (onToolbarSelectRef) {
                  onToolbarSelectRef(toolbarIndices.user + index, selectEl);
                }
              }}
              isDisablingManagement={isDisablingManagement}
            />
          )}
          {isOrgGeoDefinedColumn && (
            <OrgGeoCategory
              toolbarActive={toolbarActive}
              toolbarFocusedIndex={
                toolbarFocusedIndex !== null && toolbarIndices.orgGeo !== undefined
                  ? toolbarFocusedIndex - toolbarIndices.orgGeo
                  : null
              }
              onSelectRef={(index: number, selectEl: Maybe<any>) => {
                if (onToolbarSelectRef && toolbarIndices.orgGeo !== undefined) {
                  onToolbarSelectRef(toolbarIndices.orgGeo + index, selectEl);
                }
              }}
              onFieldsAvailable={onOrgGeoFieldsAvailable}
              onFieldClick={onFieldClick}
              onFieldItemRef={onFieldItemRef}
              active={active}
              focusedIndex={focusedIndex}
            />
          )}
          {isOrgGeoSimsDefinedColumn && (
            <OrgGeoSimsCategory
              toolbarActive={toolbarActive}
              toolbarFocusedIndex={
                toolbarFocusedIndex !== null && toolbarIndices.orgSims !== undefined
                  ? toolbarFocusedIndex - toolbarIndices.orgSims
                  : null
              }
              onSelectRef={(index: number, selectEl: Maybe<any>) => {
                if (onToolbarSelectRef && toolbarIndices.orgSims !== undefined) {
                  onToolbarSelectRef(toolbarIndices.orgSims + index, selectEl);
                }
              }}
              onFieldClick={onFieldClick}
              onFieldItemRef={onFieldItemRef}
              active={active}
              focusedIndex={focusedIndex}
            />
          )}
          {isShapefileCategory && (
            <ShapefileCategory
              toolbarActive={toolbarActive}
              toolbarFocusedIndex={
                toolbarFocusedIndex !== null && toolbarIndices.shapefile !== undefined
                  ? toolbarFocusedIndex - toolbarIndices.shapefile
                  : null
              }
              onSelectRef={(index: number, selectEl: Maybe<any>) => {
                if (onToolbarSelectRef && toolbarIndices.shapefile !== undefined) {
                  onToolbarSelectRef(toolbarIndices.shapefile + index, selectEl);
                }
              }}
            />
          )}
        </ToolbarContainer>
        <ScrollableContainer $inset={isShapefileCategory}>
          <FieldList
            active={active}
            focusedIndex={focusedIndex}
            tabsActive={tabsActive}
            tabsFocusedIndex={tabsFocusedIndex}
            isDisablingManagement={isDisablingManagement}
            onItemRef={onFieldItemRef}
            onTabItemRef={onTabItemRef}
            onFieldClick={onFieldClick}
            onFieldDoubleClick={onFieldDoubleClick}
          />
        </ScrollableContainer>
        <SearchInfoContainer isVisible={isFiltering}>
          {`Limited to ${MAX_SEARCH_RESULTS} results`}
        </SearchInfoContainer>
      </Wrapper>
    </ProjectPermissionsProvider>
  );
}

export default FilterableFieldList;

const Wrapper = styled.div`
  gap: 5px;
  max-width: var(--field-col-max-width);
  min-width: var(--field-col-min-width);
  display: grid;
  align-content: start;
  justify-content: stretch;
  background-color: var(--color-white);
  font-size: 1.3rem;
  overflow: hidden;
  grid-template-rows: auto 1fr auto;
`;

const ScrollableContainer = styled.div<{ $inset?: boolean }>`
  /* The shapefile column's panel sits 4px deeper than the list's stock 8px
     edge (ShapefileCategory's Wrapper); the list follows so the two stay on
     one line. */
  ${(props) => props.$inset && "padding: 0 var(--space-1);"}
  max-width: var(--field-col-max-width);
  min-width: var(--field-col-min-width);
  overflow: hidden overlay;
`;

const ToolbarContainer = styled.div`
  transition: max-height 0.3s ease-in-out, opacity 0.3s ease-in-out;
  max-height: 0;
  opacity: 0;
  padding: 0;
  overflow: hidden;

  ${(props) => {
    if (props.isVisible) {
      return css`
        max-height: none;
        opacity: 1;

        &:not(:empty) {
          background-color: #f4f6f6;
          border-bottom: 1px solid rgba(0, 0, 0, 0.06);
          padding-top: var(--space-2);
          padding-bottom: var(--space-2);
        }
      `;
    }
  }}
`;

const SearchInfoContainer = styled.div`
  transition: max-height 0.3s ease-in-out, opacity 0.3s ease-in-out;
  max-height: 0;
  opacity: 0;
  overflow: hidden;

  ${(props) =>
    props.isVisible &&
    css`
      max-height: none;
      opacity: 1;
      padding: var(--space-2);
    `}

  background-color: #f4f6f6;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  font-style: italic;
`;
